/**
 * Faixa de rede (item 2 de 02/09; reescrita no BUG-260, 16/09).
 *
 * Até 02/09 o app não tinha UM indicador de rede: nenhum listener
 * `online`/`offline`, nenhuma faixa, e o único "dado em cache" era prop morta
 * do `RitmoCard`. Sem rede, a pessoa via "salvando" eterno e skeleton sem
 * explicação. A faixa diz a verdade que o `settleWrite` tornou verdade: o que
 * ela fizer fica gravado localmente e sincroniza quando a rede voltar.
 *
 * ── Dois estados, duas copies (BUG-260) ─────────────────────────────────────
 *  · **offline** — `navigator.onLine === false`. Certeza; nada a tentar.
 *  · **instável** — o navegador diz "conectado" e o Firestore não alcança o
 *    servidor (sonda em `BackendReachabilityProbe`). Aqui existe o que tentar:
 *    "Reconectar" derruba e reabre os streams (`disableNetwork`/`enableNetwork`,
 *    o mesmo ciclo que o `FirestoreResumeWatcher` roda na retomada), que zera
 *    o backoff do SDK — sem isso a próxima tentativa dele pode estar a 60 s.
 *
 * ── Posição (BUG-260, a causa medida) ───────────────────────────────────────
 * A nav é `bottom-0` com altura `--nav-h + env(safe-area-inset-bottom)`, e a
 * faixa nascia em `bottom: var(--nav-h)` — SEM o inset. Num iPhone com home
 * indicator (34px) a faixa inteira (~33px) ficava atrás da nav: "tá ficando
 * debaixo da navbar e não dá pra ver" (relato de 16/09). O `FocusModeModal`
 * já usava o cálculo certo; agora esta também.
 *
 * `fixed` dentro do frame (o frame do desktop é `translate`, então é
 * containing block do `fixed` e a faixa fica contida nos 430px, como a nav).
 * Não vai no topo em fluxo: os 17 headers `sticky-top-safe` puxam a própria
 * margem para cima pela reserva de safe-area do body e cobririam a faixa pela
 * metade em aparelho com notch. `z` abaixo da nav e dos overlays — a faixa
 * informa, não disputa.
 *
 * ── O X ─────────────────────────────────────────────────────────────────────
 * Dispensar vale para ESTE episódio (`episode` do store). Rede que volta e cai
 * de novo, ou offline que vira instável, mostra a faixa outra vez — dispensar
 * não pode virar "nunca mais me avise", senão o aviso deixa de existir.
 */
import { useState } from "react";
import { RefreshCw, X } from "lucide-react";
import { IconButton } from "../ui/IconButton";
import { TextButton } from "../ui/TextButton";
import { reconnectFirestore } from "../../lib/firebase/reconnectFirestore";
import { useConnectivity } from "../../lib/net/connectivityStore";

export const OFFLINE_BANNER_COPY =
  "Sem conexão. O que você fizer fica salvo e sincroniza quando a rede voltar.";

export const UNSTABLE_BANNER_COPY =
  "Conexão instável. Mostrando o que está salvo no aparelho.";

export function OfflineBanner({
  /** Injetável para o teste não precisar de um Firestore de verdade. */
  reconnect = reconnectFirestore,
}: {
  reconnect?: () => Promise<void>;
}) {
  const { status, episode } = useConnectivity();
  const [dismissedEpisode, setDismissedEpisode] = useState<number | null>(null);
  const [reconnecting, setReconnecting] = useState(false);

  if (status === "online" || dismissedEpisode === episode) return null;

  const offline = status === "offline";

  const onReconnect = () => {
    if (reconnecting) return;
    setReconnecting(true);
    void reconnect().finally(() => setReconnecting(false));
  };

  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="offline-banner"
      data-status={status}
      className="fixed inset-x-0 bottom-[calc(var(--nav-h)+var(--focus-dock-h)+env(safe-area-inset-bottom))] z-30 flex items-center gap-2 border-t border-line bg-accent-soft py-1.5 pl-page-x pr-1 text-xs font-semibold text-ink"
    >
      <span
        aria-hidden
        className={`h-2 w-2 shrink-0 rounded-full ${offline ? "bg-ink/60" : "bg-accent"}`}
      />
      <span className="min-w-0 flex-1">{offline ? OFFLINE_BANNER_COPY : UNSTABLE_BANNER_COPY}</span>
      {!offline && (
        <TextButton
          onClick={onReconnect}
          disabled={reconnecting}
          aria-busy={reconnecting}
          className="min-h-[36px] shrink-0 px-2 text-xs text-accent"
        >
          <RefreshCw size={13} aria-hidden className={reconnecting ? "animate-spin" : ""} />
          Reconectar
        </TextButton>
      )}
      <IconButton
        icon={X}
        label="Fechar aviso de conexão"
        variant="ghost"
        size="sm"
        onClick={() => setDismissedEpisode(episode)}
        className="shrink-0 text-ink/70"
      />
    </div>
  );
}
